import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Clock3,
  Eye,
  FileText,
  Flame,
  Mail,
  Megaphone,
  MessageCircle,
  Pin,
  Plus,
  RefreshCw,
  Send,
  Sparkles,
  X,
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";

const today = new Date().toISOString().split("T")[0];

const categories = {
  important: { label: "Important", className: "important", icon: Flame },
  festival: { label: "Festival", className: "festival", icon: Sparkles },
  other: { label: "Other", className: "other", icon: Megaphone },
  general: { label: "General", className: "general", icon: Megaphone },
};

const emptyForm = {
  title: "",
  body: "",
  category: "general",
  isPinned: false,
  expiresAt: "",
  targetMode: "team",
  recipients: [],
};

const getAnnouncementCacheKey = (employee) => {
  const id = employee?._id || employee?.id;
  return id ? `employee_announcements_${id}` : null;
};

const readAnnouncementCache = (employee) => {
  const cacheKey = getAnnouncementCacheKey(employee);
  if (!cacheKey) return [];

  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
    if (!cached?.data) return [];
    return Array.isArray(cached.data) ? cached.data : [];
  } catch {
    localStorage.removeItem(cacheKey);
    return [];
  }
};

const writeAnnouncementCache = (employee, data) => {
  const cacheKey = getAnnouncementCacheKey(employee);
  if (!cacheKey) return;
  localStorage.setItem(cacheKey, JSON.stringify({ data, savedAt: Date.now() }));
};

const readAnyAnnouncementCache = () => {
  try {
    const keys = Object.keys(localStorage).filter((key) => key.startsWith("employee_announcements_"));
    let latest = null;
    keys.forEach((key) => {
      const cached = JSON.parse(localStorage.getItem(key) || "null");
      if (!cached?.data || !Array.isArray(cached.data)) return;
      if (!latest || cached.savedAt > latest.savedAt) latest = cached;
    });
    return latest?.data || [];
  } catch {
    return [];
  }
};

const demoAnnouncementWords = [
  "dummy",
  "not backend connected",
  "backend not connected",
  "not connected",
];

const isRealAnnouncement = (announcement) => {
  const searchableText = [
    announcement?.title,
    announcement?.body,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return !demoAnnouncementWords.some((word) => searchableText.includes(word));
};

const getRecipientNames = (announcement) => {
  const recipients = Array.isArray(announcement?.recipients)
    ? announcement.recipients
    : [];

  return recipients
    .map((employee) => {
      if (employee && typeof employee === "object") {
        return [employee.name, employee.employeeId ? `(${employee.employeeId})` : ""]
          .filter(Boolean)
          .join(" ");
      }
      return "";
    })
    .filter(Boolean);
};

const getAudienceLabel = (announcement) => {
  const names = getRecipientNames(announcement);
  if (names.length) return names.join(", ");
  if (announcement?.visibleTo === "team") return "All teammates";
  if (announcement?.visibleTo === "selected") return "Selected teammates";
  return "All employees";
};

const Announcements = () => {
  const { employee } = useAuth();
  const cachedAnnouncements = useMemo(() => readAnnouncementCache(employee), [employee]);

  const [announcements, setAnnouncements] = useState(() => cachedAnnouncements.length ? cachedAnnouncements : readAnyAnnouncementCache());
  const [showForm, setShowForm] = useState(false);
  const [viewAnnouncement, setViewAnnouncement] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeId, setActiveId] = useState(cachedAnnouncements[0]?._id || null);
  const [form, setForm] = useState(emptyForm);

  const isLead = employee?.role === "lead";

  useEffect(() => {
    if (!cachedAnnouncements.length) return;
    setAnnouncements(cachedAnnouncements);
    setActiveId((current) => {
      const currentStillVisible = cachedAnnouncements.some((item) => item._id === current);
      return currentStillVisible ? current : cachedAnnouncements[0]?._id || null;
    });
  }, [cachedAnnouncements]);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/announcements", {
        cache: false,
        params: { limit: 20, _t: Date.now() },
      });
      const list = Array.isArray(res.data)
        ? res.data.filter(isRealAnnouncement)
        : [];

      setAnnouncements(list);
      writeAnnouncementCache(employee, list);
      setActiveId((current) => {
        const currentStillVisible = list.some((item) => item._id === current);
        return currentStillVisible ? current : list[0]?._id || null;
      });
    } catch (err) {
      console.error("Failed to load announcements:", err);
    }
  }, [employee]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    if (!isLead) return;

    let active = true;
    api.get("/api/employee/team/members")
      .then((res) => {
        if (!active) return;
        const currentEmployeeId = String(employee?._id || employee?.id || "");
        const members = Array.isArray(res.data) ? res.data : [];
        setTeamMembers(
          members.filter((member) =>
            member.isActive !== false &&
            String(member._id || member.id) !== currentEmployeeId,
          ),
        );
      })
      .catch((err) => {
        console.error("Failed to load team members for announcements:", err);
        if (active) setTeamMembers([]);
      });

    return () => {
      active = false;
    };
  }, [employee, isLead]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (!document.hidden) fetchAnnouncements();
    }, 3000);
    return () => window.clearInterval(id);
  }, [fetchAnnouncements]);

  const stats = useMemo(() => {
    const unread = announcements.filter((a) => !a.isRead).length;
    const pinned = announcements.filter((a) => a.isPinned).length;
    const comments = announcements.reduce((sum, a) => sum + (a.comments?.length || 0), 0);

    return {
      total: announcements.length,
      unread,
      pinned,
      comments,
    };
  }, [announcements]);

  const activeAnnouncement = useMemo(() => {
    return announcements.find((a) => a._id === activeId) || announcements[0];
  }, [activeId, announcements]);

  const handleMarkRead = async (id) => {
    const previousAnnouncements = announcements;
    setAnnouncements((prev) => {
      const nextAnnouncements = prev.map((a) => (a._id === id ? { ...a, isRead: true } : a));
      writeAnnouncementCache(employee, nextAnnouncements);
      return nextAnnouncements;
    });
    try {
      await api.patch(`/api/employee/announcements/${id}/read`);
    } catch (err) {
      setAnnouncements(previousAnnouncements);
      writeAnnouncementCache(employee, previousAnnouncements);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const selectedRecipients = form.targetMode === "selected" ? form.recipients : [];
    const payload = {
      ...form,
      recipients: selectedRecipients,
    };

    const optimisticId = `pending-${Date.now()}`;
    const optimisticAnnouncement = {
      _id: optimisticId,
      ...payload,
      visibleTo: selectedRecipients.length ? "selected" : "team",
      createdAt: new Date().toISOString(),
      postedByName: employee?.name || "You",
      isRead: true,
      comments: [],
    };
    const previousAnnouncements = announcements;

    setAnnouncements((prev) => {
      const nextAnnouncements = [optimisticAnnouncement, ...prev].slice(0, 20);
      writeAnnouncementCache(employee, nextAnnouncements);
      return nextAnnouncements;
    });
    setActiveId(optimisticId);
    setForm(emptyForm);
    setShowForm(false);
    try {
      await api.post("/api/employee/announcements/create", payload);
      window.setTimeout(fetchAnnouncements, 0);
    } catch (err) {
      setAnnouncements(previousAnnouncements);
      writeAnnouncementCache(employee, previousAnnouncements);
    }
  };

  const handleViewAnnouncement = (announcement) => {
    setActiveId(announcement._id);
    setViewAnnouncement(announcement);
    if (!announcement.isRead) handleMarkRead(announcement._id);
  };

  return (
    <div className="leaves-premium-wrapper announcements-page text-[var(--text-primary)]">
      <main className="leave-reference-shell announcements-shell">
        <div className="leave-reference-layout announcements-grid">
          <div className="leave-reference-main">
            <div className="announcements-hero">
            <div className="announcements-hero-copy">
              <h1>Announcements</h1>
              <p>Stay informed with important updates and news.</p>
              <div className="premium-meta-row announcements-meta-row">
                <span>
                  <CalendarDays size={15} />
                  {new Date().toLocaleDateString("en-IN", { weekday: "long" })}
                </span>
                <span>
                  <Clock3 size={15} />
                  Last updated: Just now
                </span>
              </div>
            </div>

            <div className="announcements-hero-actions">
              <AnnouncementMetric label="Total" value={stats.total} icon={Megaphone} />
              <AnnouncementMetric label="Unread" value={stats.unread} icon={Mail} />
              <AnnouncementMetric label="Pinned" value={stats.pinned} icon={Pin} />
            </div>
          </div>

            <section className="announcements-card announcements-feed-card">
              <div className="premium-card-header announcements-feed-head">
                <div>
                  <span className="premium-section-kicker">Company Feed</span>
                  <h2>Latest updates</h2>
                </div>
                {isLead ? (
                  <button className="premium-primary-button" onClick={() => setShowForm(true)}>
                    <Plus size={16} />
                    New post
                  </button>
                ) : (
                  <span className="announcement-count-pill">{stats.total} posts</span>
                )}
              </div>

              <div className="announcements-feed-list">
                {announcements.length === 0 ? (
                  <div className="announcements-empty">
                    <AnnouncementEmptyArt />
                    <strong>No announcements yet</strong>
                    <span>Fresh updates will appear here.</span>
                  </div>
                ) : (
                  announcements.map((announcement) => (
                    <AnnouncementItem
                      key={announcement._id}
                      announcement={announcement}
                      active={activeAnnouncement?._id === announcement._id}
                      showAudience={isLead}
                      onSelect={() => {
                        setActiveId(announcement._id);
                        if (!announcement.isRead) handleMarkRead(announcement._id);
                      }}
                      onView={() => handleViewAnnouncement(announcement)}
                    />
                  ))
                )}
              </div>
            </section>
          </div>

          <aside className="leave-reference-side announcements-side-stack">
              <section className="announcements-card announcement-summary-card">
                <div className="premium-card-header">
                  <div>
                    <span className="premium-section-kicker">Quick Summary</span>
                    <h2>Overview</h2>
                  </div>
                </div>

                <div className="announcement-summary-grid">
                  <SummaryTile label="Unread" value={stats.unread} icon={Mail} />
                  <SummaryTile label="Pinned" value={stats.pinned} icon={Pin} />
                  <SummaryTile label="Comments" value={stats.comments} icon={MessageCircle} />
                  <SummaryTile label="Posts" value={stats.total} icon={FileText} />
                </div>
              </section>
          </aside>
        </div>
      </main>

      {showForm && (
        <div className="announcement-modal-backdrop" role="presentation">
          <form className="announcement-modal" onSubmit={handleCreate}>
            <div className="announcement-modal-head">
              <div>
                <span className="premium-section-kicker">Lead Post</span>
                <h2>New announcement</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <input
              placeholder="Announcement title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />

            <div className="announcement-modal-row">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="general">General</option>
                <option value="important">Important</option>
                <option value="festival">Festival</option>
                <option value="other">Other</option>
              </select>

              <input
                type="date"
                min={today}
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
            </div>

            <textarea
              placeholder="Write announcement..."
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
            />

            <div className="announcement-recipient-box">
              <div>
                <span className="premium-section-kicker">Send to</span>
                <strong>{form.targetMode === "selected" ? "Selected teammates" : "All teammates"}</strong>
              </div>

              <div className="announcement-recipient-toggle">
                <button
                  type="button"
                  className={form.targetMode === "team" ? "active" : ""}
                  onClick={() => setForm({ ...form, targetMode: "team", recipients: [] })}
                >
                  All
                </button>
                <button
                  type="button"
                  className={form.targetMode === "selected" ? "active" : ""}
                  onClick={() => setForm({ ...form, targetMode: "selected" })}
                >
                  Select
                </button>
              </div>

              {form.targetMode === "selected" && (
                <div className="announcement-recipient-list">
                  {teamMembers.length === 0 ? (
                    <span className="announcement-recipient-empty">No teammates found.</span>
                  ) : (
                    teamMembers.map((member) => {
                      const memberId = member._id || member.id;
                      const checked = form.recipients.includes(memberId);
                      return (
                        <label key={memberId} className="announcement-recipient-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setForm((current) => ({
                                ...current,
                                recipients: e.target.checked
                                  ? [...current.recipients, memberId]
                                  : current.recipients.filter((id) => id !== memberId),
                              }));
                            }}
                          />
                          <span>
                            <strong>{member.name}</strong>
                            <small>{member.employeeId || member.email || member.team}</small>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            <label className="announcement-pin-check">
              <input
                type="checkbox"
                checked={form.isPinned}
                onChange={(e) => setForm({ ...form, isPinned: e.target.checked })}
              />
              Pin announcement
            </label>

            <div className="announcement-modal-actions">
              <button type="button" className="announcement-secondary-button" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button className="premium-primary-button" disabled={form.targetMode === "selected" && form.recipients.length === 0}>
                <Send size={16} />
                Publish
              </button>
            </div>
          </form>
        </div>
      )}

      {viewAnnouncement && (
        <AnnouncementViewModal
          announcement={viewAnnouncement}
          showAudience={isLead}
          onClose={() => setViewAnnouncement(null)}
        />
      )}
    </div>
  );
};

const AnnouncementEmptyArt = () => (
  <div className="announcement-empty-art" aria-hidden="true">
    <span>
      <Megaphone size={30} />
    </span>
  </div>
);

const AnnouncementMetric = ({ label, value, icon: Icon }) => (
  <div className="announcement-hero-metric">
    {Icon && (
      <span className="announcement-metric-icon">
        <Icon size={18} />
      </span>
    )}
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const AnnouncementItem = ({ announcement, active, showAudience, onSelect, onView }) => {
  const meta = categories[announcement.category] || categories.general;
  const Icon = meta.icon;

  return (
    <article
      className={`announcement-feed-item ${active ? "active" : ""} ${announcement.isRead ? "" : "unread"}`}
    >
      <span className={`announcement-feed-icon ${meta.className}`}>
        <Icon size={17} />
      </span>
      <button className="announcement-feed-copy" type="button" onClick={onSelect}>
        <span className="announcement-feed-title">
          {announcement.isPinned && <Pin size={14} />}
          {announcement.title}
        </span>
        <span>{announcement.body}</span>
        {showAudience && (
          <span className="announcement-audience-line">
            Assigned to {getAudienceLabel(announcement)}
          </span>
        )}
      </button>
      <span className="announcement-feed-actions">
        <span className="announcement-feed-date">
          {formatDate(announcement.createdAt)}
        </span>
        <button type="button" className="announcement-view-button" onClick={onView}>
          <Eye size={14} />
          View
        </button>
      </span>
    </article>
  );
};

const AnnouncementViewModal = ({ announcement, showAudience, onClose }) => {
  const meta = categories[announcement.category] || categories.general;
  const Icon = meta.icon;

  return (
    <div className="announcement-modal-backdrop" role="presentation">
      <article className="announcement-modal announcement-view-modal">
        <div className="announcement-modal-head">
          <div>
            <span className={`announcement-category-pill ${meta.className}`}>
              <Icon size={14} />
              {meta.label}
            </span>
            <h2>{announcement.title}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close announcement view">
            <X size={18} />
          </button>
        </div>

        <div className="announcement-view-body">
          {announcement.body}
        </div>

        <div className="announcement-detail-meta">
          <span>By {announcement.postedByName || "Team"}</span>
          {showAudience && <span>Assigned to {getAudienceLabel(announcement)}</span>}
          <span>
            <CalendarDays size={14} />
            {formatLongDate(announcement.createdAt)}
          </span>
          {announcement.expiresAt && <span>Expires {formatLongDate(announcement.expiresAt)}</span>}
        </div>
      </article>
    </div>
  );
};

const SummaryTile = ({ label, value, icon: Icon }) => (
  <div className="announcement-summary-tile">
    <span className="announcement-summary-icon">{Icon && <Icon size={18} />}</span>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </div>
);

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
};

const formatLongDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export default Announcements;
